import React, { forwardRef, useMemo } from "react";
import styles from "./Text.module.scss";
import type {
  TextProps,
  TextElement,
  TextVariant,
  Spacing,
  TextOpacity,
  FontWeightToken,
  FontFamilyToken,
  FontSize,
  FontSizeToken,
  FontWeight,
  FontFamily,
} from "./Text.types";

// Default element mapping basaed on varient
const varientElementMap: Record<TextVariant, TextElement> = {
  "display-xl": "h1",
  "display-lg": "h1",
  "display-md": "h1",
  h1: "h1",
  h2: "h2",
  h3: "h3",
  h4: "h4",
  h5: "h5",
  h6: "h6",
  "body-xl": "p",
  "body-lg": "p",
  "body-md": "p",
  "body-sm": "p",
  "body-xs": "p",
  caption: "span",
  overline: "span",
};
// Font size token values (matching SCSS)
const fontSizeTokens: Record<FontSizeToken, string> = {
  xs: "0.75rem",
  sm: "0.875rem",
  base: "1rem",
  md: "1.125rem",
  lg: "1.25rem",
  xl: "1.5rem",
  "2xl": "1.875rem",
  "3xl": "2.25rem",
  "4xl": "3rem",
  "5xl": "3.75rem",
  "6xl": "4.5rem",
  "7xl": "6rem",
  "8xl": "8rem",
  "9xl": "9rem",
};

// Font weight token values
const fontWeightTokens: Record<FontWeightToken, number> = {
  thin: 100,
  extralight: 200,
  light: 300,
  regular: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
  extrabold: 800,
  black: 900,
};

// Font family token values
const fontFamilyTokens: Record<FontFamilyToken, string> = {
  sans: "ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
  serif: "ui-serif, Georgia, Cambria, 'Times New Roman', Times, serif",
  mono: "ui-monospace, SFMono-Regular, 'SF Mono', Menlo, Consolas, 'Liberation Mono', monospace",
  poppins: "Poppins",
  carlito: "carlito",
};
const getSpacingClass = (
  prefix: string,
  value: Spacing | undefined,
): string => {
  if (value === undefined) return "";
  return styles[`${prefix}-${value}`] || "";
};

const getOpacityClass = (value: TextOpacity | undefined): string => {
  if (value === undefined) return "";
  return styles[`opacity-${value}`] || "";
};

const resolveFontSize = (
  fontSize: FontSize | undefined,
): string | undefined => {
  if (fontSize === undefined) return undefined;

  // Check if its a token
  if (typeof fontSize === "string" && fontSize in fontSizeTokens) {
    return fontSizeTokens[fontSize as FontSizeToken];
  }
  // if its a number
  if (typeof fontSize === "number") {
    return `${fontSize}px`;
  }
  // otherwise its a custom string (e.g 1.5rem)
  return fontSize;
};

const resolveFontWeight = (
  fontWeight: FontWeight | undefined,
): number | undefined => {
  if (fontWeight === undefined) return undefined;

  // Check if its a token
  if (typeof fontWeight === "string" && fontWeight in fontWeightTokens) {
    return fontWeightTokens[fontWeight as FontWeightToken];
  }
  // if its a numeric value
  return fontWeight as number;
};

const resolveFontFamily = (
  fontFamily: FontFamily | undefined,
): string | undefined => {
  if (fontFamily === undefined) return undefined;

  if (typeof fontFamily === "string" && fontFamily in fontFamilyTokens) {
    return fontFamilyTokens[fontFamily as FontFamilyToken];
  }
  // if its a custom font family string
  return fontFamily;
};

export const Text = forwardRef<HTMLElement, TextProps>(
  (
    {
      children,
      as,
      variant = "body-md",
      // New dynamic font properties
      fontSize,
      responsiveFontSize,
      fontWeight,
      responsiveFontWeight,
      fontFamily,
      color = "primary",
      opacity,
      customColor,
      align,
      transform,
      lineHeight,
      letterSpacing,
      truncate = false,
      maxLines,
      noSelect = false,
      noWrap = false,
      italic = false,
      underline = false,
      lineThrough = false,
      responsive,
      className = "",
      style,
      gradient,
      id,
      dataTestId,
      ariaLabel,
      onClick,
      m,
      mx,
      my,
      mt,
      mr,
      mb,
      ml,
      p,
      px,
      py,
      pt,
      pr,
      pb,
      pl,
    },
    ref,
  ) => {
    // Determine the HTML element to render
    const Element = (as || varientElementMap[variant]) as React.ElementType;

    // Build calss names
    const classNames = useMemo(() => {
      const classes = [
        styles.text,
        !fontSize && !responsiveFontSize ? styles[`variant-${variant}`] : "",
        color && !customColor && !gradient ? styles[`color-${color}`] : "",
        getOpacityClass(opacity),
        // Font family token classes
        fontFamily && fontFamily in fontFamilyTokens
          ? styles[`font-${fontFamily}`]
          : "",
        // Responsive font size classes
        responsiveFontSize ? styles["responsive-font-size"] : "",
        fontWeight ? styles[`weight-${fontWeight}`] : "",
        align ? styles[`align-${align}`] : "",
        transform ? styles[`transform-${transform}`] : "",
        lineHeight ? styles[`line-height-${lineHeight}`] : "",
        letterSpacing ? styles[`letter-spacing-${letterSpacing}`] : "",
        truncate ? styles.truncate : "",
        maxLines ? styles[`max-lines-${maxLines}`] : "",
        noSelect ? styles["no-select"] : "",
        noWrap ? styles["no-wrap"] : "",
        italic ? styles.italic : "",
        underline ? styles.underline : "",
        lineThrough ? styles[`line-through`] : "",
        gradient ? styles.gradient : "",
        onClick ? styles.clickable : "",
        className,
        getSpacingClass("m", m),
        getSpacingClass("mx", mx),
        getSpacingClass("my", my),
        getSpacingClass("mt", mt),
        getSpacingClass("mr", mr),
        getSpacingClass("mb", mb),
        getSpacingClass("ml", ml),
        getSpacingClass("p", p),
        getSpacingClass("px", px),
        getSpacingClass("py", py),
        getSpacingClass("pt", pt),
        getSpacingClass("pr", pr),
        getSpacingClass("pb", pb),
        getSpacingClass("pl", pl),
      ];
      if (responsive) {
        Object.entries(responsive).forEach(
          ([breakpoint, responsiveVarient]) => {
            if (responsiveVarient) {
              classes.push(
                styles[`${breakpoint}-variant-${responsiveVarient}`],
              );
            }
          },
        );
      }
      return classes.filter(Boolean).join(" ");
    }, [
      variant,
      fontSize,
      responsiveFontSize,
      fontWeight,
      fontFamily,
      color,
      opacity,
      customColor,
      gradient,

      align,
      transform,
      lineHeight,
      letterSpacing,
      truncate,
      maxLines,
      noSelect,
      noWrap,
      italic,
      underline,
      lineThrough,
      responsive,
      className,
      onClick,
      m,
      mx,
      my,
      mt,
      mr,
      mb,
      ml,
      p,
      px,
      py,
      pt,
      pr,
      pb,
      pl,
    ]);

    const inlineStyles = useMemo(() => {
      const baseStyles: React.CSSProperties = { ...style };
      if (customColor) {
        baseStyles.color = customColor;
      }
      if (gradient) {
        baseStyles.backgroundImage = gradient;
      }
      // apply dynamic font size
      const resolvedFontSize = resolveFontSize(fontSize);
      if (resolvedFontSize) {
        baseStyles.fontSize = resolvedFontSize;
      }
      // apply dynamic font weight
      const resolvedFontWeight = resolveFontWeight(fontWeight);
      if (resolvedFontWeight) {
        baseStyles.fontWeight = resolvedFontWeight;
      }
      // Add dynamic font family (if not a token)
      if (fontFamily && !(fontFamily in fontFamilyTokens)) {
        baseStyles.fontFamily = resolveFontFamily(fontFamily);
      }

      if (responsiveFontSize) {
        if (responsiveFontSize.base) {
          baseStyles["--font-size-base" as any] = resolveFontSize(
            responsiveFontSize.base,
          );
        }
        if (responsiveFontSize.sm) {
          baseStyles["--font-size-sm" as any] = resolveFontSize(
            responsiveFontSize.sm,
          );
        }
        if (responsiveFontSize.md) {
          baseStyles["--font-size-md" as any] = resolveFontSize(
            responsiveFontSize.md,
          );
        }
        if (responsiveFontSize.lg) {
          baseStyles["--font-size-lg" as any] = resolveFontSize(
            responsiveFontSize.lg,
          );
        }
        if (responsiveFontSize.xl) {
          baseStyles["--font-size-xl" as any] = resolveFontSize(
            responsiveFontSize.xl,
          );
        }
      }
      // Apply responsive font weights as CSS custom properties
      if (responsiveFontWeight) {
        if (responsiveFontWeight.base) {
          baseStyles["--font-weight-base" as any] = resolveFontWeight(
            responsiveFontWeight.base,
          );
        }
        if (responsiveFontWeight.sm) {
          baseStyles["--font-weight-sm" as any] = resolveFontWeight(
            responsiveFontWeight.sm,
          );
        }
        if (responsiveFontWeight.md) {
          baseStyles["--font-weight-md" as any] = resolveFontWeight(
            responsiveFontWeight.md,
          );
        }
        if (responsiveFontWeight.lg) {
          baseStyles["--font-weight-lg" as any] = resolveFontWeight(
            responsiveFontWeight.lg,
          );
        }
        if (responsiveFontWeight.xl) {
          baseStyles["--font-weight-xl" as any] = resolveFontWeight(
            responsiveFontWeight.xl,
          );
        }
      }
      return baseStyles;
    }, [
      style,
      customColor,
      gradient,
      fontSize,
      fontWeight,
      fontFamily,
      fontFamily,
      responsiveFontSize,
      responsiveFontWeight,
    ]);

    return (
      <Element
        ref={ref}
        id={id}
        className={classNames}
        style={inlineStyles}
        data-testid={dataTestId}
        aria-label={ariaLabel}
        onClick={onClick}
      >
        {children}
      </Element>
    );
  },
);

export default Text;

Text.displayName = "Text";
